import * as React from 'react';
import { cn } from '@/lib/cn';

// UI_BUILD_SPEC §2.9. Underline tabs with counts. Implemented as a roving
// tabindex: only the active tab is in the tab order and arrows move between
// them, which is the WAI-ARIA tabs pattern and stops a 7-tab queue filter from
// costing seven Tab presses to skip.

export interface TabItem {
  value: string;
  label: string;
  count?: number;
}

interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  'aria-label'?: string;
}

export function Tabs({ items, value, onChange, className, 'aria-label': ariaLabel }: TabsProps) {
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);

  function handleKeyDown(event: React.KeyboardEvent, index: number) {
    const lastIndex = items.length - 1;
    let next = -1;
    if (event.key === 'ArrowRight') next = index === lastIndex ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index === 0 ? lastIndex : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = lastIndex;
    if (next < 0) return;
    event.preventDefault();
    onChange(items[next].value);
    refs.current[next]?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn('flex items-center gap-1 overflow-x-auto border-b border-line', className)}
    >
      {items.map((item, index) => {
        const isActive = item.value === value;
        return (
          <button
            key={item.value}
            ref={(node) => { refs.current[index] = node; }}
            role="tab"
            type="button"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(item.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'inline-flex h-10 shrink-0 items-center gap-2 border-b-2 px-4 text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              isActive
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-ink-subtle hover:text-ink-muted',
            )}
          >
            {item.label}
            {item.count !== undefined && (
              <span
                className={cn(
                  'rounded-full px-1.5 text-xs font-medium',
                  isActive ? 'bg-brand-100 text-brand-700' : 'text-ink-subtle',
                )}
              >
                {item.count.toLocaleString()}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
